import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { defaultPage } from './constants'
import type { PageConfigsType, PageEntityType } from './types'

const STORAGE_KEY = 'navigation'
const MAX_HISTORY_LENGTH = 2

interface NavigationContextType {
  currentPage: PageEntityType
  history: PageEntityType[]
  goToPage: <GPage extends keyof PageConfigsType>(
    ...args: PageConfigsType[GPage] extends undefined
      ? [page: GPage]
      : [page: GPage, options: PageConfigsType[GPage]]
  ) => void
  setOptions: (
    options: Partial<
      Exclude<PageConfigsType[PageEntityType['page']], undefined>
    >,
  ) => void
  goBack: () => void
}

interface NavigationStateType {
  currentPage: PageEntityType
  history: PageEntityType[]
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
)

const loadNavigationState = () => {
  try {
    const rawData = localStorage.getItem(STORAGE_KEY)
    if (rawData) {
      const data = JSON.parse(rawData) as NavigationStateType
      return data
    }
  } catch (error) {
    console.error('Error parsing navigation state', error)
  }
  return null
}

const saveNavigationState = (state: NavigationStateType) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    console.error('Error saving navigation state', error)
  }
}

export const NavigationProvider = (props: { children: React.ReactNode }) => {
  const initialState = loadNavigationState()
  const [currentPage, setCurrentPage] = useState<PageEntityType>(
    initialState?.currentPage || defaultPage,
  )
  const [history, setHistory] = useState<PageEntityType[]>(
    initialState?.history || [defaultPage],
  )

  const goToPage = useCallback(
    <GPage extends keyof PageConfigsType>(
      ...args: PageConfigsType[GPage] extends undefined
        ? [page: GPage]
        : [page: GPage, options: PageConfigsType[GPage]]
    ) => {
      const newEntry =
        args.length === 1
          ? ({ page: args[0] } as PageEntityType)
          : ({ page: args[0], options: args[1] } as PageEntityType)

      console.log(`Navigating to page:`, newEntry)

      if (newEntry.page === currentPage.page) {
        if ('options' in currentPage) {
          console.log('Updating options for current page:', newEntry)
          setCurrentPage(newEntry)
          window.history.replaceState(newEntry, '')
        }
        return
      }

      setHistory((prev) => {
        const updatedHistory = [...prev, currentPage]
        console.log('Updated history:', updatedHistory)
        return updatedHistory.length > MAX_HISTORY_LENGTH
          ? updatedHistory.slice(-MAX_HISTORY_LENGTH)
          : updatedHistory
      })

      setCurrentPage(newEntry)
      window.history.pushState(newEntry, '')
    },
    [currentPage],
  )

  const setOptions = useCallback(
    (
      options: Partial<
        Exclude<PageConfigsType[PageEntityType['page']], undefined>
      >,
    ) => {
      if (!('options' in currentPage)) {
        console.warn(`Page ${currentPage.page} does not support options.`)
        return
      }
      const updatedPage = { ...currentPage, options }
      console.log('Updating page options:', updatedPage)
      // @ts-expect-error types
      setCurrentPage(updatedPage)
      window.history.replaceState(updatedPage, '')
    },
    [currentPage],
  )

  const goBack = useCallback(() => {
    if (history.length === 0) {
      console.error('No more history to go back.')
      setCurrentPage(defaultPage)
      return
    }
    const prevPage = history[history.length - 1]
    const newHistory = history.slice(0, -1)
    console.log('Going back to previous page:', prevPage)
    setCurrentPage(prevPage)
    setHistory(newHistory)
  }, [history])

  useEffect(() => {
    if (
      history.length === 0 &&
      ![
        'connectToPrivateTable',
        'createPrivateTable',
        'table',
        defaultPage.page,
      ].includes(currentPage.page)
    ) {
      setHistory([defaultPage])
    }
  }, [history, currentPage])

  useEffect(() => {
    saveNavigationState({ currentPage, history })
  }, [currentPage, history])

  useEffect(() => {
    console.log('History updated:', history)
    if (!window.history.state && history.length) {
      window.history.replaceState(history[history.length - 1], '')
    }
  }, [history])

  useEffect(() => {
    window.addEventListener('popstate', goBack)

    return () => {
      window.removeEventListener('popstate', goBack)
    }
  }, [goBack])

  return (
    <NavigationContext.Provider
      value={{ currentPage, history, goToPage, setOptions, goBack }}
    >
      {props.children}
    </NavigationContext.Provider>
  )
}

export const useNavigation = () => {
  const context = useContext(NavigationContext)
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }
  return context
}
